import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function TrainingSign() {
  const { tid } = useParams();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState("/cass-logo-group.png");

  const load = () => api.get(`/training/${tid}`).then((r) => setRec(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Training record not found"));
  useEffect(() => { load(); api.get("/branding").then((r) => r.data.logo_url && setLogoUrl(r.data.logo_url)).catch(() => {}); /* eslint-disable-next-line */ }, [tid]);

  const sign = async () => {
    if (!name.trim()) { toast.error("Type your full name to sign"); return; }
    setSaving(true);
    try { await api.post(`/training/${tid}/sign`, { signed_name: name.trim() }); toast.success("Training signed"); setName(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not sign"); }
    finally { setSaving(false); }
  };

  const downloadRecord = async () => {
    try { const r = await api.get(`/training/${tid}/certificate`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error("Record unavailable"); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6"><div className="text-center"><p className="text-slate-500" data-testid="training-sign-error">{err}</p></div></div>;
  if (!rec) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const signed = rec.signature;
  const canSign = user?.employee_id === rec.employee_id && !signed;
  const hasDetails = rec.details && rec.details.replace(/<[^>]+>/g, "").trim();

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4" data-testid="training-sign-page">
      <div className="max-w-2xl mx-auto">
        <img src={logoUrl} alt="Company logo" className="h-11 w-auto object-contain mb-6 bg-white rounded-lg px-3 py-2 inline-block" />
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <p className="text-xs uppercase tracking-wider text-[#2EA3FD] font-semibold">Internal Training Record</p>
          <h1 className="text-2xl font-extrabold text-[#0B1A2F] mt-1" data-testid="training-sign-title">{rec.course}</h1>
          <div className="mt-4 divide-y divide-slate-100 text-sm">
            <Field label="Employee">{rec.employee_name}</Field>
            {rec.provider && <Field label="Training delivered by">{rec.provider}</Field>}
            {rec.completed_date && <Field label="Completed">{fmtDate(rec.completed_date)}</Field>}
            {rec.expiry_date && <Field label="Expires / renewal">{fmtDate(rec.expiry_date)}</Field>}
          </div>

          {hasDetails && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Training received</p>
              <div className="text-sm text-slate-700 border border-slate-200 rounded-lg p-4 [&_ul]:list-disc [&_ul]:pl-5 [&_h2]:font-semibold [&_h2]:text-[#0B1A2F]" data-testid="training-sign-details" dangerouslySetInnerHTML={{ __html: rec.details }} />
            </div>
          )}

          <div className="mt-6 border-t border-slate-200 pt-5">
            <p className="text-sm text-slate-700">{rec.agreement_statement || "I confirm I have received and understood this training."}</p>
            {signed ? (
              <div className="mt-4">
                <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="training-sign-done"><Check size={16} />Signed by {signed.signed_name} on {fmtDate((signed.signed_at || "").slice(0, 10))}</p>
                <Button variant="outline" className="mt-3" onClick={downloadRecord} data-testid="training-sign-download">View signed record</Button>
              </div>
            ) : canSign ? (
              <div className="mt-4 space-y-3">
                <div><Label className="text-xs text-slate-500">Type your full name to sign</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="training-sign-name-input" /></div>
                <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6] font-semibold" onClick={sign} disabled={saving} data-testid="training-sign-confirm-btn"><ShieldCheck size={16} className="mr-1.5" />{saving ? "Signing…" : "Agree & sign"}</Button>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-400" data-testid="training-sign-readonly">This training is awaiting the employee's e-signature.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-[#0B1A2F] text-right">{children}</span>
  </div>
);
